<template>
  <main class="promoter-orders" :class="{ 'has-tips': tips }">
    <van-nav-bar
      :title="pageTitle"
      left-arrow
      right-text="退出"
      fixed
      placeholder
      safe-area-inset-top
      @click-left="logout"
      @click-right="logout"
    />
    <van-pull-refresh v-model="refreshing" @refresh="refresh">
      <van-list v-model="loading" :finished="finished" :error.sync="error"
        error-text="加载失败，点击重试" :finished-text="records.length ? '没有更多了' : ''"
        @load="load">
        <article v-for="(item, index) in records" :key="`${item.orderSn}-${index}`" class="order">
          <div class="top-row">
            <p class="goods-name">{{ item.goodsName }}</p>
            <p class="amount" :class="{ closed: isClosedOrder(item) }">
              <span>
                ¥{{ formatMoney(isClosedOrder(item) ? item.payAmount : item.commission) }}
              </span>
              <span
                v-if="!isClosedOrder(item)" class="performance"
              >(业绩:{{ formatMoney(item.payAmount) }})</span>
            </p>
          </div>
          <p class="phone">{{ item.phoneNumber || '--' }}</p>
          <div class="bottom-row">
            <p class="order-number">
              {{ item.orderSn }}<strong v-if="orderLabel(item)">【{{ orderLabel(item) }}】</strong>
            </p>
            <time>{{ item.payTime || '--' }}</time>
          </div>
        </article>
      </van-list>
      <van-empty v-if="finished && !records.length" description="暂无推广业绩" />
    </van-pull-refresh>
    <footer v-if="tips">{{ tips }}</footer>
  </main>
</template>

<script>
import {
  List, PullRefresh, Empty, NavBar, Toast,
} from 'vant';
import api from '@/apis/promoter';
import {
  clearPromoterSession, formatMoney, getPromoterMobile, isClosedOrder, isLoginExpired,
  isNonPromoter, NON_PROMOTER_MESSAGE,
} from '@/utils/promoter';

export default {
  components: {
    [List.name]: List,
    [PullRefresh.name]: PullRefresh,
    [Empty.name]: Empty,
    [NavBar.name]: NavBar,
  },
  data() {
    return {
      records: [],
      tips: '',
      page: 1,
      size: 20,
      loading: false,
      refreshing: false,
      finished: false,
      error: false,
      requesting: false,
      disposed: false,
    };
  },
  computed: {
    pageTitle() {
      return [getPromoterMobile(), '的推广业绩'].join('');
    },
  },
  beforeDestroy() { this.disposed = true; },
  methods: {
    formatMoney,
    isClosedOrder,
    orderLabel(item) {
      return isClosedOrder(item) ? item.statusDesc : (item.validDesc || '').replace(/[【】]/g, '');
    },
    logout() {
      clearPromoterSession();
      this.$router.replace('/web/promoter/login');
    },
    refresh() {
      if (this.requesting) { this.refreshing = false; return; }
      this.page = 1;
      this.tips = '';
      this.error = false;
      this.finished = false;
      this.loading = true;
      this.load();
    },
    async load() {
      if (this.requesting) return;
      this.requesting = true;
      try {
        const result = await api.orders({ page: this.page, size: this.size });
        if (this.disposed) return;
        if (isLoginExpired(result) || isNonPromoter(result)) {
          this.finished = true;
          Toast(isNonPromoter(result) ? NON_PROMOTER_MESSAGE : '登录已过期，请重新登录');
          this.logout();
          return;
        }
        if (Number(result.code) !== 0 || result.success === false) throw new Error(result.msg || '加载失败');
        const data = result.data || {};
        if (!Array.isArray(data.records)) throw new Error('业绩数据格式异常');
        // 防止后端忽略分页参数时无限重复追加第一页。
        if (data.page !== undefined && Number(data.page) !== this.page) throw new Error('分页返回异常，请稍后重试');
        this.records = this.page === 1 ? data.records : this.records.concat(data.records);
        if (this.page === 1 || data.tips !== undefined) {
          this.tips = typeof data.tips === 'string' ? data.tips : '';
        }
        if (data.totalPage !== undefined) {
          this.finished = this.page >= Number(data.totalPage);
        } else if (data.total !== undefined) {
          this.finished = this.records.length >= Number(data.total);
        } else {
          this.finished = data.records.length < this.size;
        }
        this.finished = this.finished || data.records.length === 0;
        this.page += 1;
      } catch (error) {
        if (this.disposed) return;
        if (error.response && error.response.status === 401) {
          this.finished = true;
          Toast('登录已过期，请重新登录');
          this.logout();
        } else {
          this.error = true;
          Toast(error.message === 'Network Error' ? '网络异常，请点击重试' : error.message);
        }
      } finally {
        this.loading = false;
        this.refreshing = false;
        this.requesting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.promoter-orders {
  min-height: 100vh;
  background: #fff;
  color: #999;
  font-size: 12px;
  &.has-tips { padding-bottom: calc(52px + env(safe-area-inset-bottom)); }
  ::v-deep .van-nav-bar--fixed { z-index: 1001; }
  ::v-deep .van-nav-bar__title { color: #333; font-weight: 600; }
  ::v-deep .van-nav-bar .van-icon { color: #333; }
  ::v-deep .van-nav-bar__text { color: #333; }
  .order { padding: 8px 15px; border-bottom: 2px solid #e3e3e3; }
  .top-row, .bottom-row { display: flex; justify-content: space-between; gap: 6px; }
  .goods-name {
    flex: 1;
    min-width: 0;
    font-size: 13px;
    line-height: 1.5;
    color: #666;
    overflow-wrap: anywhere;
  }
  .amount { flex-shrink: 0; color: #f00; line-height: 1.5; font-size: 13px; }
  .performance { color: #333; }
  .amount.closed { color: #999; }
  .phone { text-align: right; line-height: 1.7; }
  .bottom-row { flex-wrap: wrap; align-items: baseline; line-height: 1.6; }
  .order-number { overflow-wrap: anywhere; }
  strong { color: #666; font-weight: 500; margin-left: 4px; }
  time { margin-left: auto; white-space: nowrap; }
  footer {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 2;
    background: #f2f2f2;
    padding: 15px;
    padding-bottom: calc(15px + env(safe-area-inset-bottom));
    color: #ed7800;
    font-size: 14px;
  }
}
</style>
